import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService, GridBaseQuery, GridHelperService, PortalContextProvider } from '@core';
import { KendoDataSourceResultDto } from '../dtos/kendo-data-source-result.dto';
import { StatusResponseDto } from '../dtos/status-response.dto';
import { StatusDetailResponseDto } from '../dtos/status-detail-response.dto';
import { StatusCategoryResponseDto } from '../dtos/status-category-response.dto';
import { CreateStatusRequestDto } from '../dtos/create-status-request.dto';
import { UpdateStatusRequestDto } from '../dtos/update-status-request.dto';
import { ToggleStatusActiveRequestDto } from '../dtos/toggle-status-active-request.dto';
import { StatusUsageResponseDto } from '../dtos/status-usage-response.dto';

@Injectable({ providedIn: 'root' })
export class StatusApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _portalContext = inject(PortalContextProvider);
  private readonly _baseUrl = this._config.versionedApiUrl;

  private get _url(): string {
    return `${this._baseUrl}/${this._portalContext.currentPortalType()}/statuses`;
  }

  public getAllStatuses(): Observable<KendoDataSourceResultDto<StatusResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<StatusResponseDto>>(this._url);
  }

  public getStatusesWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<StatusResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResultDto<StatusResponseDto>>(this._url, { params });
  }

  public getStatusDetail(id: string): Observable<StatusDetailResponseDto> {
    return this._http.get<StatusDetailResponseDto>(`${this._url}/${id}`);
  }

  public getCategories(): Observable<StatusCategoryResponseDto[]> {
    return this._http.get<StatusCategoryResponseDto[]>(`${this._url}/categories`);
  }

  public createStatus(request: CreateStatusRequestDto): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(this._url, request);
  }

  public updateStatus(id: string, request: UpdateStatusRequestDto): Observable<void> {
    return this._http.put<void>(`${this._url}/${id}`, request);
  }

  public deleteStatus(id: string): Observable<void> {
    return this._http.delete<void>(`${this._url}/${id}`);
  }

  public toggleStatusActive(id: string, request: ToggleStatusActiveRequestDto): Observable<void> {
    return this._http.post<void>(`${this._url}/${id}/toggle-active`, request);
  }

  public getStatusUsage(id: string): Observable<StatusUsageResponseDto> {
    return this._http.get<StatusUsageResponseDto>(`${this._url}/${id}/usage`);
  }
}
